import React, { Suspense, lazy } from 'react';
import { Navigate } from 'react-router-dom';
import PageWrapper from './components/PageWrapper';
import FullWidthPageWrapper from './components/FullWidthPageWrapper';
import Loader from './components/Loader';
import SectionErrorBoundary from './components/common/MyPages/SectionErrorBoundary';

// Layouts (keep these eager as they're needed immediately)
import AuthLayout from './layouts/Auth';
import DashboardLayout from './layouts/Dashboard';

// Guards (keep these eager as they're needed immediately)
import AuthGuard from './components/guards/AuthGuard';
import AdminGuard from './components/guards/AdminGuard';
import SalespersonGuard from './components/guards/SalespersonGuard';
import PartnerGuard from './components/guards/PartnerGuard';
import ProductionGuard from './components/guards/ProductionGuard';

// Contexts (keep these eager)
import { LeadsContextDashboardProvider } from './contexts/dashboard/LeadsDashboardContext';

// Auth components (lazy load - not needed on initial page load)
const SignIn = lazy(() => import('./pages/auth/SignIn'));
const RestorePassword = lazy(() => import('./pages/auth/RestorePassword'));
const NewPassword = lazy(() => import('./pages/auth/NewPassword'));
const ConfirmInvite = lazy(() => import('./pages/auth/ConfirmInvite'));
const Page404 = lazy(() => import('./pages/auth/Page404'));

// Protected routes (lazy load - only loaded after authentication)
const UsersList = lazy(() => import('./components/AdminPage/UsersList'));
const SitesList = lazy(() => import('./components/AdminPage/SitesList'));
const HemsidorList = lazy(() => import('./components/AdminPage/HemsidorList'));
const DomainPage = lazy(() => import('./components/UserPage/DomainPage'));
// const LeadsPage = lazy(() => import('./components/UserPage/LeadsPage'));
const ClientLeadManagement = lazy(() => import('./components/ClientPage/ClientLeadManagement'));
const LeadsList = lazy(() => import('./components/AdminPage/LeadsList'));
const DashboardPage = lazy(() => import('./components/UserPage/DashboardPage'));
const AdminDashboardPage = lazy(() => import('./components/AdminPage/AdminDashboardPage'));
const RankingPage = lazy(() => import('./components/AdminPage/RankingPage'));
const UserStatisticsPage = lazy(() => import('./components/AdminPage/UserStatisticsPage'));
const ChangePasswordPage = lazy(() => import('./components/UserPage/settings/ChangePasswordPage'));
const GuidesPage = lazy(() => import('./components/UserPage/settings/GuidesPage'));
const IntegrationsPage = lazy(() => import('./components/UserPage/settings/IntegrationsPage'));
const Sales = lazy(() => import('./components/AdminPage/SalesPage'));
const RankingDashboardPage = lazy(() => import('./components/SalespersonPage/RankingDashboardPage'));
const SalespersonDefaultDashboardPage = lazy(() =>
  import('./components/SalespersonPage/SalespersonDefaultDashboardPage'),
);
const ClientHemsidorList = lazy(() => import('./components/ClientPage/ClientHemsidorList'));
const AdminClientList = lazy(() => import('./components/AdminPage/AdminClientList'));
const AssignSiteOwner = lazy(() => import('./components/AdminPage/AssignSiteOwner'));
const InfoPage = lazy(() => import('./components/AdminPage/InfoPage'));
const InfoForm = lazy(() => import('./components/AdminPage/InfoForm'));
// const LeadPage = lazy(() => import('./components/UserPage/LeadPage'));
const OptimizationClientList = lazy(() => import('./components/AdminPage/OptimizationClientList'));
const ClientsCompaniesList = lazy(() => import('./components/AdminPage/ClientsCompaniesList'));
const PromotionsListContainer = lazy(() => import('./containers/proposals/ProposalsListContainer'));
const ProposalPage = lazy(() => import('./containers/proposals/ProposalPage'));
const AdminInvoicesPage = lazy(() => import('./components/AdminPage/AdminInvoicesPage'));
const ClientInvoicePage = lazy(() => import('./components/ClientInvoicePage'));
const LeadExpectationPage = lazy(() => import('./components/AdminPage/LeadsDashboard/LeadExpectationPage'));
const LeadDetailsPage = lazy(() => import('./components/AdminPage/LeadManagement/LeadDetailsPage'));
const DpaAgreementPage = lazy(() => import('./components/ClientPage/DpaAgreementPage'));
const DpaAdminPage = lazy(() => import('./components/AdminPage/DpaAdminPage'));

// Analytics Components (lazy load)
const AdminAnalyticsPage = lazy(() => import('./components/Analytics/AdminAnalyticsPage'));
const ClientAnalyticsPage = lazy(() => import('./components/Analytics/ClientAnalyticsPage'));
const SalespersonAnalyticsPage = lazy(() => import('./components/Analytics/SalespersonAnalyticsPage'));

// Admin Data Management Components (lazy load)
const CitiesList = lazy(() => import('./components/AdminPage/CitiesList'));
const BlocklistManagement = lazy(() => import('./components/AdminPage/BlocklistManagement'));
const UtmOverviewPage = lazy(() => import('./components/AdminPage/UtmOverview/UtmOverviewPage'));
const OurLeadsPage = lazy(() => import('./components/AdminPage/OurLeads/OurLeadsPage'));
const OurLeadDetailsPage = lazy(() => import('./components/AdminPage/OurLeads/OurLeadDetailsPage'));

// Sprint 2 - Mina Sidor (My Pages) - new admin/salesperson/produktion shell
const MyPagesUsersListPage = lazy(() =>
  import('./components/AdminPage/MyPages/Drift/Users/UsersListPage'),
);
const MyPagesUserFormPage = lazy(() =>
  import('./components/AdminPage/MyPages/Drift/Users/UserFormPage'),
);
const MyPagesProductsListPage = lazy(() =>
  import('./components/AdminPage/MyPages/Drift/Products/ProductsListPage'),
);
const MyPagesProductFormPage = lazy(() =>
  import('./components/AdminPage/MyPages/Drift/Products/ProductFormPage'),
);
const MyPagesNotificationSettingsPage = lazy(() =>
  import('./components/AdminPage/MyPages/Drift/Notifications/NotificationSettingsPage'),
);
const MyPagesCustomerListPage = lazy(() =>
  import('./components/AdminPage/MyPages/Leverans/Customers/CustomerListPage'),
);
const MyPagesCustomerFormPage = lazy(() =>
  import('./components/AdminPage/MyPages/Leverans/Customers/CustomerFormPage'),
);
const MyPagesCustomerCardPage = lazy(() =>
  import('./components/AdminPage/MyPages/Leverans/Customers/CustomerCardPage'),
);
const MyPagesSubscriptionsListPage = lazy(() =>
  import('./components/AdminPage/MyPages/CRM/Subscriptions/SubscriptionsListPage'),
);
const MyPagesProspectsListPage = lazy(() =>
  import('./components/AdminPage/MyPages/CRM/Prospects/ProspectsListPage'),
);
const MyPagesBillingDecisionListPage = lazy(() =>
  import('./components/AdminPage/MyPages/CRM/Subscriptions/BillingDecisionListPage'),
);
// Temporary page: manual mapping of historical Oneflow contracts.
const MyPagesOneflowUnmappedPage = lazy(() =>
  import('./components/AdminPage/MyPages/CRM/Oneflow/OneflowUnmappedPage'),
);
const MyPagesSubscriptionWizardPage = lazy(() =>
  import('./components/AdminPage/MyPages/CRM/Subscriptions/SubscriptionWizardPage'),
);
const MyPagesSubscriptionDetailPage = lazy(() =>
  import('./components/AdminPage/MyPages/CRM/Subscriptions/SubscriptionDetailPage'),
);
// Sprint 3
const MyPagesPipelinePage = lazy(() =>
  import('./components/AdminPage/MyPages/CRM/Pipeline/PipelinePage'),
);
const MyPagesSalesDashboardPage = lazy(() =>
  import('./components/AdminPage/MyPages/CRM/SalesDashboard/SalesDashboardPage'),
);
const MyPagesRetentionPage = lazy(() =>
  import('./components/AdminPage/MyPages/CRM/Retention/RetentionPage'),
);
const MyPagesAdminActivityOverviewPage = lazy(() =>
  import('./components/AdminPage/MyPages/CRM/Activities/AdminActivityOverviewPage'),
);
const MyPagesPersonalActivityPage = lazy(() =>
  import('./components/AdminPage/MyPages/CRM/Activities/PersonalActivityPage'),
);
const MyPagesBudgetsPage = lazy(() =>
  import('./components/AdminPage/MyPages/CRM/Budgets/BudgetsPage'),
);
// Sprint 4 + Sprint 5 — new pages
const MyPagesFortnoxStatusPage = lazy(() =>
  import('./components/AdminPage/MyPages/Drift/Integrations/FortnoxStatusPage'),
);
const MyPagesFortnoxOAuthCallbackPage = lazy(() =>
  import('./components/AdminPage/MyPages/Drift/Integrations/FortnoxOAuthCallbackPage'),
);
const MyPagesSveaStatusPage = lazy(() =>
  import('./components/AdminPage/MyPages/Drift/Integrations/SveaStatusPage'),
);
const MyPagesRebuildAllStatusPage = lazy(() =>
  import('./components/AdminPage/MyPages/Drift/Integrations/RebuildAllStatusPage'),
);
const MyPagesEventLogsPage = lazy(() =>
  import('./components/AdminPage/MyPages/Drift/Events/EventLogsPage'),
);
const MyPagesProductionListPage = lazy(() =>
  import('./components/AdminPage/MyPages/Leverans/Production/ProductionListPage'),
);
const MyPagesProductionDetailPage = lazy(() =>
  import('./components/AdminPage/MyPages/Leverans/Production/ProductionDetailPage'),
);
const MyPagesAdminInvoiceOverviewPage = lazy(() =>
  import('./components/AdminPage/MyPages/Leverans/Invoices/AdminInvoiceOverviewPage'),
);
const MyPagesCompanyOverviewPage = lazy(() =>
  import('./components/AdminPage/MyPages/Leverans/CompanyOverview/CompanyOverviewPage'),
);
const MyPagesArrBudgetPage = lazy(() =>
  import('./components/AdminPage/MyPages/CRM/Arr/ArrBudgetPage'),
);
const MyPagesSalesAnalysisPage = lazy(() =>
  import('./components/AdminPage/MyPages/Leverans/SalesAnalysis/SalesAnalysisPage'),
);
const MyPagesMrrAnalysisPage = lazy(() =>
  import('./components/AdminPage/MyPages/Leverans/MrrAnalysis/MrrAnalysisPage'),
);
const MyPagesLeadsOverviewPage = lazy(() =>
  import('./components/AdminPage/MyPages/Leverans/LeadsOverview/LeadsOverviewPage'),
);

// Wrapper component for lazy loaded routes with Suspense + error boundary
const LazyComponent = ({ children }) => (
  <SectionErrorBoundary>
    <Suspense fallback={<Loader />}>
      {children}
    </Suspense>
  </SectionErrorBoundary>
);

// Settings ("Inställningar") sub-pages shared by every role. The settings
// sidebar swaps in for these routes (see settingsItems.js / Dashboard layout).
// `settings` (index) defaults to the change-password page.
const buildSettingsRoutes = () => [
  {
    path: 'settings',
    element: (
      <LazyComponent>
        <PageWrapper logo="true" title="Inställningar">
          <ChangePasswordPage />
        </PageWrapper>
      </LazyComponent>
    ),
  },
  {
    path: 'settings/losenord',
    element: (
      <LazyComponent>
        <PageWrapper logo="true" title="Byt lösenord">
          <ChangePasswordPage />
        </PageWrapper>
      </LazyComponent>
    ),
  },
  {
    path: 'settings/guider',
    element: (
      <LazyComponent>
        <PageWrapper logo="true" title="Guider & Introduktion">
          <GuidesPage />
        </PageWrapper>
      </LazyComponent>
    ),
  },
  {
    path: 'settings/integrationer',
    element: (
      <LazyComponent>
        <PageWrapper logo="true" title="Google Calendar-synk">
          <IntegrationsPage />
        </PageWrapper>
      </LazyComponent>
    ),
  },
  {
    path: 'settings/notifikationer',
    element: (
      <LazyComponent>
        <FullWidthPageWrapper title="Notifikationsinställningar">
          <MyPagesNotificationSettingsPage />
        </FullWidthPageWrapper>
      </LazyComponent>
    ),
  },
];

const routes = [
  {
    path: '/',
    element: <Navigate to="/auth/sign-in" />,
  },
  {
    // Fortnox OAuth bootstrap callback. Configured via FORTNOX_REDIRECT_URI
    // (default http://localhost:3000/fortnox/callback). The admin must already
    // be signed in for the backend exchange call to succeed.
    path: 'fortnox/callback',
    element: (
      <LazyComponent>
        <PageWrapper title="Fortnox OAuth-callback">
          <MyPagesFortnoxOAuthCallbackPage />
        </PageWrapper>
      </LazyComponent>
    ),
  },
  {
    path: 'auth',
    element: <AuthLayout />,
    children: [
      {
        path: 'sign-in',
        element: (
          <LazyComponent>
            <PageWrapper title="Sign In">
              <SignIn />
            </PageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'restore-password',
        element: (
          <LazyComponent>
            <PageWrapper title="Restore Password">
              <RestorePassword />
            </PageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'restore-new-password',
        element: (
          <LazyComponent>
            <PageWrapper title="New Password">
              <NewPassword />
            </PageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'invite-confirmation',
        element: (
          <LazyComponent>
            <PageWrapper title="Confirm Invite">
              <ConfirmInvite />
            </PageWrapper>
          </LazyComponent>
        ),
      },
    ],
  },
  {
    path: 'client',
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      { path: '/client', element: <Navigate to="/client/dashboard" /> },
      {
        path: 'dashboard',
        element: (
          <LazyComponent>
            <PageWrapper logo="true" title="Dashboard">
              <DashboardPage />
            </PageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'pages',
        element: (
          <LazyComponent>
            <PageWrapper logo="true" title="Landningssidor">
              <DomainPage />
            </PageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'hemsidor',
        element: (
          <LazyComponent>
            <PageWrapper logo="true" title="Hemsidor">
              <ClientHemsidorList />
            </PageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'invoices',
        element: (
          <LazyComponent>
            <PageWrapper logo="true" title="Fakturor page">
              <ClientInvoicePage />
            </PageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'leads',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper logo="true" title="Kundförfrågningar">
              <ClientLeadManagement />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'leads/:id',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Lead Detaljer">
              <LeadDetailsPage />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      ...buildSettingsRoutes(),
      {
        path: 'avtal',
        element: (
          <LazyComponent>
            <PageWrapper logo="true" title="Avtal">
              <DpaAgreementPage />
            </PageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'statistik',
        children: [
          {
            path: 'hemsida',
            element: (
              <LazyComponent>
                <PageWrapper logo="true" title="Statistik - Hemsidor">
                  <ClientAnalyticsPage siteType="brandpages" />
                </PageWrapper>
              </LazyComponent>
            ),
          },
        ],
      },
      {
        path: 'info',
        children: [
          {
            path: 'upload',
            element: (
              <LazyComponent>
                <PageWrapper logo="true" title="Upload images">
                  <InfoPage />
                </PageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'form',
            element: (
              <LazyComponent>
                <PageWrapper logo="true" title="Form">
                  <InfoForm />
                </PageWrapper>
              </LazyComponent>
            ),
          },
        ],
      },
    ],
  },
  {
    path: 'salesperson',
    element: (
      <AuthGuard>
        <SalespersonGuard>
          <DashboardLayout />
        </SalespersonGuard>
      </AuthGuard>
    ),
    children: [
      { path: '/salesperson', element: <Navigate to="/salesperson/dashboard/default" /> },
      {
        path: 'dashboard',
        children: [
          {
            path: 'default',
            element: (
              <LazyComponent>
                <FullWidthPageWrapper title="Säljarteamet">
                  <SalespersonDefaultDashboardPage />
                </FullWidthPageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'sales',
            element: (
              <LazyComponent>
                <PageWrapper logo="true" title="Sälj">
                  <Sales />
                </PageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'ranking',
            element: (
              <LazyComponent>
                <PageWrapper logo="true" title="Ranking">
                  <RankingDashboardPage />
                </PageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'user-statistics',
            element: (
              <LazyComponent>
                <PageWrapper logo="true" title="User statistics">
                  <UserStatisticsPage />
                </PageWrapper>
              </LazyComponent>
            ),
          },
        ],
      },
      {
        path: 'statistik',
        children: [
          {
            path: 'landningsida',
            element: (
              <LazyComponent>
                <PageWrapper logo="true" title="Statistik - Landningssidor">
                  <SalespersonAnalyticsPage siteType="sites" />
                </PageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'hemsida',
            element: (
              <LazyComponent>
                <PageWrapper logo="true" title="Statistik - Hemsidor">
                  <SalespersonAnalyticsPage siteType="brandpages" />
                </PageWrapper>
              </LazyComponent>
            ),
          },
        ],
      },
      {
        path: 'sites',
        element: (
          <LazyComponent>
            <PageWrapper logo="true" title="Landningssidor">
              <DomainPage />
            </PageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'hemsidor',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Hemsidor">
              <HemsidorList />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'client',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Inlogg kunder">
              <AdminClientList />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'client/:id',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Inlogg kunder">
              <AssignSiteOwner />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'kundföretag',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Kundföretag">
              <ClientsCompaniesList />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      ...buildSettingsRoutes(),
      {
        path: 'leads',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Kundförfrågningar">
              <LeadsList />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'leads/:id',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Lead Detaljer">
              <LeadDetailsPage />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'users',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Användare">
              <UsersList />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'users/:id',
        element: (
          <LazyComponent>
            <PageWrapper logo="true" title="Användare">
              <AssignSiteOwner />
            </PageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'proposal',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Proposals">
              <PromotionsListContainer />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'proposal/:id',
        element: (
          <LazyComponent>
            <PageWrapper logo="true" title="Proposals page">
              <ProposalPage />
            </PageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'fakturalista',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Fakturalista">
              <AdminInvoicesPage />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'invoices',
        handle: { crumb: 'Fakturaöversikt' },
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Fakturaöversikt">
              <MyPagesAdminInvoiceOverviewPage />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'data-management',
        children: [
          {
            path: 'cities',
            element: (
              <LazyComponent>
                <FullWidthPageWrapper title="Städer Hantering">
                  <CitiesList />
                </FullWidthPageWrapper>
              </LazyComponent>
            ),
          },
        ],
      },

      // Sprint 2 - Mina Sidor: Säljarvy
      {
        path: 'crm',
        handle: { crumb: 'CRM' },
        children: [
          {
            path: 'pipeline',
            handle: { crumb: 'Pipeline' },
            element: (
              <LazyComponent>
                <FullWidthPageWrapper title="Pipeline">
                  <MyPagesPipelinePage basePath="/salesperson" />
                </FullWidthPageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'abonnemang',
            handle: { crumb: 'Abonnemang' },
            children: [
              {
                index: true,
                element: (
                  <LazyComponent>
                    <FullWidthPageWrapper title="Abonnemang">
                      <MyPagesSubscriptionsListPage basePath="/salesperson" />
                    </FullWidthPageWrapper>
                  </LazyComponent>
                ),
              },
              {
                path: 'nytt',
                handle: { crumb: 'Nytt abonnemang' },
                element: (
                  <LazyComponent>
                    <FullWidthPageWrapper title="Nytt abonnemang">
                      <MyPagesSubscriptionWizardPage basePath="/salesperson" />
                    </FullWidthPageWrapper>
                  </LazyComponent>
                ),
              },
              {
                path: ':id',
                handle: { crumb: 'Abonnemang' },
                element: (
                  <LazyComponent>
                    <FullWidthPageWrapper title="Abonnemang">
                      <MyPagesSubscriptionDetailPage basePath="/salesperson" />
                    </FullWidthPageWrapper>
                  </LazyComponent>
                ),
              },
            ],
          },
          {
            path: 'prospekt',
            handle: { crumb: 'Prospekt' },
            element: (
              <LazyComponent>
                <FullWidthPageWrapper title="Prospekt">
                  <MyPagesProspectsListPage basePath="/salesperson" />
                </FullWidthPageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'saljardashboard',
            handle: { crumb: 'Min dashboard' },
            element: (
              <LazyComponent>
                <FullWidthPageWrapper title="Min dashboard">
                  <MyPagesSalesDashboardPage basePath="/salesperson" />
                </FullWidthPageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'retention',
            handle: { crumb: 'Retention' },
            element: (
              <LazyComponent>
                <FullWidthPageWrapper title="Retention">
                  <MyPagesRetentionPage basePath="/salesperson" />
                </FullWidthPageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'aktivitet',
            handle: { crumb: 'Aktiviteter' },
            element: (
              <LazyComponent>
                <FullWidthPageWrapper title="Aktiviteter">
                  <MyPagesPersonalActivityPage basePath="/salesperson" />
                </FullWidthPageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'arr-budget',
            handle: { crumb: 'ARR & budget' },
            element: (
              <LazyComponent>
                <FullWidthPageWrapper title="ARR & budget">
                  <MyPagesArrBudgetPage basePath="/salesperson" />
                </FullWidthPageWrapper>
              </LazyComponent>
            ),
          },
        ],
      },
      {
        path: 'integrationer',
        handle: { crumb: 'Integrationer' },
        element: <Navigate to="/salesperson/settings#integrationer" replace />,
      },
      {
        path: 'leverans',
        handle: { crumb: 'Leverans' },
        children: [
          {
            path: 'kundlista',
            handle: { crumb: 'Kundlista' },
            children: [
              {
                index: true,
                element: (
                  <LazyComponent>
                    <FullWidthPageWrapper title="Kundlista">
                      <MyPagesCustomerListPage basePath="/salesperson" />
                    </FullWidthPageWrapper>
                  </LazyComponent>
                ),
              },
              {
                path: 'nytt',
                handle: { crumb: 'Ny kund' },
                element: (
                  <LazyComponent>
                    <FullWidthPageWrapper title="Ny kund">
                      <MyPagesCustomerFormPage mode="create" basePath="/salesperson" />
                    </FullWidthPageWrapper>
                  </LazyComponent>
                ),
              },
              {
                path: ':id',
                handle: { crumb: 'Kundkort' },
                element: (
                  <LazyComponent>
                    <FullWidthPageWrapper title="Kundkort">
                      <MyPagesCustomerCardPage basePath="/salesperson" />
                    </FullWidthPageWrapper>
                  </LazyComponent>
                ),
              },
              {
                path: ':id/redigera',
                handle: { crumb: 'Redigera kund' },
                element: (
                  <LazyComponent>
                    <FullWidthPageWrapper title="Redigera kund">
                      <MyPagesCustomerFormPage mode="edit" basePath="/salesperson" />
                    </FullWidthPageWrapper>
                  </LazyComponent>
                ),
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: 'produktion',
    element: (
      <AuthGuard>
        <ProductionGuard>
          <DashboardLayout />
        </ProductionGuard>
      </AuthGuard>
    ),
    children: [
      { path: '/produktion', element: <Navigate to="/produktion/produktionsvy" /> },
      {
        path: 'produktionsvy',
        handle: { crumb: 'Produktionsvy' },
        children: [
          {
            index: true,
            element: (
              <LazyComponent>
                <FullWidthPageWrapper title="Produktionsvy">
                  <MyPagesProductionListPage basePath="/produktion" />
                </FullWidthPageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: ':id',
            handle: { crumb: 'Produktionsdetalj' },
            element: (
              <LazyComponent>
                <FullWidthPageWrapper title="Produktionsdetalj">
                  <MyPagesProductionDetailPage basePath="/produktion" />
                </FullWidthPageWrapper>
              </LazyComponent>
            ),
          },
        ],
      },
      {
        path: 'leverans',
        handle: { crumb: 'Leverans' },
        children: [
          {
            path: 'kundlista',
            handle: { crumb: 'Kundlista' },
            children: [
              {
                index: true,
                element: (
                  <LazyComponent>
                    <FullWidthPageWrapper title="Kundlista">
                      <MyPagesCustomerListPage basePath="/produktion" />
                    </FullWidthPageWrapper>
                  </LazyComponent>
                ),
              },
              {
                path: ':id',
                handle: { crumb: 'Kundkort' },
                element: (
                  <LazyComponent>
                    <FullWidthPageWrapper title="Kundkort">
                      <MyPagesCustomerCardPage basePath="/produktion" />
                    </FullWidthPageWrapper>
                  </LazyComponent>
                ),
              },
            ],
          },
        ],
      },
      {
        path: 'notifikationer',
        handle: { crumb: 'Notifikationsinställningar' },
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Notifikationsinställningar">
              <MyPagesNotificationSettingsPage />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'produkter',
        handle: { crumb: 'Produktregister' },
        children: [
          {
            index: true,
            element: (
              <LazyComponent>
                <FullWidthPageWrapper title="Produktregister">
                  <MyPagesProductsListPage basePath="/produktion/produkter" />
                </FullWidthPageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'nytt',
            handle: { crumb: 'Ny produkt' },
            element: (
              <LazyComponent>
                <FullWidthPageWrapper title="Ny produkt">
                  <MyPagesProductFormPage mode="create" basePath="/produktion/produkter" />
                </FullWidthPageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: ':id',
            handle: { crumb: 'Produkt' },
            element: (
              <LazyComponent>
                <FullWidthPageWrapper title="Produkt">
                  <MyPagesProductFormPage mode="edit" basePath="/produktion/produkter" />
                </FullWidthPageWrapper>
              </LazyComponent>
            ),
          },
        ],
      },
      {
        path: 'users',
        handle: { crumb: 'Användare' },
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Användare">
              <UsersList />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'users/:id',
        handle: { crumb: 'Användare' },
        element: (
          <LazyComponent>
            <PageWrapper logo="true" title="Användare">
              <AssignSiteOwner />
            </PageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'data-management',
        handle: { crumb: 'Datahantering' },
        children: [
          {
            path: 'cities',
            handle: { crumb: 'Städer' },
            element: (
              <LazyComponent>
                <FullWidthPageWrapper title="Städer Hantering">
                  <CitiesList />
                </FullWidthPageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'blocklist',
            handle: { crumb: 'Blocklista' },
            element: (
              <LazyComponent>
                <FullWidthPageWrapper title="Blocklista Hantering">
                  <BlocklistManagement />
                </FullWidthPageWrapper>
              </LazyComponent>
            ),
          },
        ],
      },
      ...buildSettingsRoutes(),
    ],
  },
  {
    path: 'admin',
    element: (
      <AuthGuard>
        <AdminGuard>
          <DashboardLayout />
        </AdminGuard>
      </AuthGuard>
    ),
    children: [
      { path: '/admin', element: <Navigate to="/admin/dashboard/default" /> },
      {
        path: 'dashboard',
        children: [
          {
            path: 'default',
            element: (
              <LazyComponent>
                <FullWidthPageWrapper title="Säljarteamet">
                  <SalespersonDefaultDashboardPage />
                </FullWidthPageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'sales',
            element: (
              <LazyComponent>
                <PageWrapper logo="true" title="Sälj">
                  <Sales />
                </PageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'ranking',
            element: (
              <LazyComponent>
                <PageWrapper logo="true" title="Ranking">
                  <RankingPage />
                </PageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'user-statistics',
            element: (
              <LazyComponent>
                <PageWrapper logo="true" title="User statistics">
                  <UserStatisticsPage />
                </PageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'leads',
            element: (
              <LazyComponent>
                <PageWrapper logo="true" title="Leads">
                  <LeadsContextDashboardProvider>
                    <LeadExpectationPage />
                  </LeadsContextDashboardProvider>
                </PageWrapper>
              </LazyComponent>
            ),
          },
        ],
      },
      {
        path: 'statistik',
        children: [
          {
            path: 'landningsida',
            element: (
              <LazyComponent>
                <PageWrapper logo="true" title="Statistik - Landningssidor">
                  <AdminAnalyticsPage siteType="sites" />
                </PageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'hemsida',
            element: (
              <LazyComponent>
                <PageWrapper logo="true" title="Statistik - Hemsidor">
                  <AdminAnalyticsPage siteType="brandpages" />
                </PageWrapper>
              </LazyComponent>
            ),
          },
        ],
      },
      {
        path: 'sites',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Landningssidor">
              <SitesList />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'hemsidor',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Hemsidor">
              <HemsidorList />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'client',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Inlogg kunder">
              <AdminClientList />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'client/:id',
        element: (
          <LazyComponent>
            <PageWrapper logo="true" title="Inlogg kunder">
              <AssignSiteOwner />
            </PageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'kundföretag',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Kundföretag">
              <ClientsCompaniesList />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'our-leads',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Våra leads">
              <OurLeadsPage />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'our-leads/:id',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Lead Detaljer">
              <OurLeadDetailsPage />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'leads',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Kundförfrågningar">
              <LeadsList />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'leads/:id',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Lead Detaljer">
              <LeadDetailsPage />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      ...buildSettingsRoutes(),
      {
        path: 'users',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Användare">
              <UsersList />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'users/:id',
        element: (
          <LazyComponent>
            <PageWrapper logo="true" title="Användare">
              <AssignSiteOwner />
            </PageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'optimering',
        children: [
          {
            path: ':status',
            element: (
              <LazyComponent>
                <FullWidthPageWrapper title="Optimering">
                  <OptimizationClientList />
                </FullWidthPageWrapper>
              </LazyComponent>
            ),
          },
        ],
      },
      {
        path: 'proposal',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Proposals">
              <PromotionsListContainer />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'proposal/:id',
        element: (
          <LazyComponent>
            <PageWrapper logo="true" title="Proposals page">
              <ProposalPage />
            </PageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'invoices',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Fakturor page">
              <AdminInvoicesPage />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'data-management',
        children: [
          {
            path: 'cities',
            element: (
              <LazyComponent>
                <FullWidthPageWrapper title="Städer Hantering">
                  <CitiesList />
                </FullWidthPageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'blocklist',
            element: (
              <LazyComponent>
                <FullWidthPageWrapper title="Blocklista Hantering">
                  <BlocklistManagement />
                </FullWidthPageWrapper>
              </LazyComponent>
            ),
          },
        ],
      },
      {
        path: 'utm-overview',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="UTM Spårning">
              <UtmOverviewPage />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'dpa',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="DPA Avtal">
              <DpaAdminPage />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'integrationer',
        handle: { crumb: 'Integrationer' },
        element: <Navigate to="/admin/settings#integrationer" replace />,
      },

      // Sprint 2 - Mina Sidor: CRM
      {
        path: 'crm',
        handle: { crumb: 'CRM' },
        children: [
          {
            path: 'pipeline',
            handle: { crumb: 'Pipeline' },
            element: (
              <LazyComponent>
                <FullWidthPageWrapper title="Pipeline">
                  <MyPagesPipelinePage basePath="/admin" />
                </FullWidthPageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'abonnemang',
            handle: { crumb: 'Abonnemang' },
            children: [
              {
                index: true,
                element: (
                  <LazyComponent>
                    <FullWidthPageWrapper title="Abonnemang">
                      <MyPagesSubscriptionsListPage basePath="/admin" />
                    </FullWidthPageWrapper>
                  </LazyComponent>
                ),
              },
              {
                path: 'faktureringsbeslut',
                handle: { crumb: 'Faktureringsbeslut' },
                element: (
                  <LazyComponent>
                    <FullWidthPageWrapper title="Faktureringsbeslut">
                      <MyPagesBillingDecisionListPage basePath="/admin" />
                    </FullWidthPageWrapper>
                  </LazyComponent>
                ),
              },
              {
                path: 'nytt',
                handle: { crumb: 'Nytt abonnemang' },
                element: (
                  <LazyComponent>
                    <FullWidthPageWrapper title="Nytt abonnemang">
                      <MyPagesSubscriptionWizardPage basePath="/admin" />
                    </FullWidthPageWrapper>
                  </LazyComponent>
                ),
              },
              {
                path: ':id',
                handle: { crumb: 'Abonnemang' },
                element: (
                  <LazyComponent>
                    <FullWidthPageWrapper title="Abonnemang">
                      <MyPagesSubscriptionDetailPage basePath="/admin" />
                    </FullWidthPageWrapper>
                  </LazyComponent>
                ),
              },
            ],
          },
          {
            path: 'oneflow-mappning',
            handle: { crumb: 'Oneflow-mappning' },
            element: (
              <LazyComponent>
                <FullWidthPageWrapper title="Oneflow-mappning">
                  <MyPagesOneflowUnmappedPage basePath="/admin" />
                </FullWidthPageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'prospekt',
            handle: { crumb: 'Prospekt' },
            element: (
              <LazyComponent>
                <FullWidthPageWrapper title="Prospekt">
                  <MyPagesProspectsListPage basePath="/admin" />
                </FullWidthPageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'saljardashboard',
            handle: { crumb: 'Säljardashboard' },
            element: (
              <LazyComponent>
                <FullWidthPageWrapper title="Säljardashboard">
                  <MyPagesSalesDashboardPage basePath="/admin" />
                </FullWidthPageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'arr',
            handle: { crumb: 'ARR & budget' },
            element: (
              <LazyComponent>
                <FullWidthPageWrapper title="ARR & budget">
                  <MyPagesArrBudgetPage basePath="/admin" />
                </FullWidthPageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'retention',
            handle: { crumb: 'Retention' },
            element: (
              <LazyComponent>
                <FullWidthPageWrapper title="Retention">
                  <MyPagesRetentionPage basePath="/admin" />
                </FullWidthPageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'aktivitet',
            handle: { crumb: 'Aktivitetsöversikt' },
            element: (
              <LazyComponent>
                <FullWidthPageWrapper title="Aktivitetsöversikt">
                  <MyPagesAdminActivityOverviewPage basePath="/admin" />
                </FullWidthPageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'budgetar',
            handle: { crumb: 'Budgetar' },
            element: (
              <LazyComponent>
                <FullWidthPageWrapper title="Budgetar">
                  <MyPagesBudgetsPage />
                </FullWidthPageWrapper>
              </LazyComponent>
            ),
          },
        ],
      },

      // Sprint 2 - Mina Sidor: Leverans
      {
        path: 'leverans',
        handle: { crumb: 'Leverans' },
        children: [
          {
            path: 'kundlista',
            handle: { crumb: 'Kundlista' },
            children: [
              {
                index: true,
                element: (
                  <LazyComponent>
                    <FullWidthPageWrapper title="Kundlista">
                      <MyPagesCustomerListPage basePath="/admin" />
                    </FullWidthPageWrapper>
                  </LazyComponent>
                ),
              },
              {
                path: 'nytt',
                handle: { crumb: 'Ny kund' },
                element: (
                  <LazyComponent>
                    <FullWidthPageWrapper title="Ny kund">
                      <MyPagesCustomerFormPage mode="create" basePath="/admin" />
                    </FullWidthPageWrapper>
                  </LazyComponent>
                ),
              },
              {
                path: ':id',
                handle: { crumb: 'Kundkort' },
                element: (
                  <LazyComponent>
                    <FullWidthPageWrapper title="Kundkort">
                      <MyPagesCustomerCardPage basePath="/admin" />
                    </FullWidthPageWrapper>
                  </LazyComponent>
                ),
              },
              {
                path: ':id/redigera',
                handle: { crumb: 'Redigera kund' },
                element: (
                  <LazyComponent>
                    <FullWidthPageWrapper title="Redigera kund">
                      <MyPagesCustomerFormPage mode="edit" basePath="/admin" />
                    </FullWidthPageWrapper>
                  </LazyComponent>
                ),
              },
            ],
          },
          {
            path: 'bolags-kpi',
            handle: { crumb: 'Bolagsöversikt KPI' },
            element: (
              <LazyComponent>
                <FullWidthPageWrapper title="Bolagsöversikt KPI">
                  <MyPagesCompanyOverviewPage />
                </FullWidthPageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'produktion',
            handle: { crumb: 'Produktionsvy' },
            children: [
              {
                index: true,
                element: (
                  <LazyComponent>
                    <FullWidthPageWrapper title="Produktionsvy">
                      <MyPagesProductionListPage basePath="/admin" />
                    </FullWidthPageWrapper>
                  </LazyComponent>
                ),
              },
              {
                path: ':id',
                handle: { crumb: 'Produktionsdetalj' },
                element: (
                  <LazyComponent>
                    <FullWidthPageWrapper title="Produktionsdetalj">
                      <MyPagesProductionDetailPage basePath="/admin" />
                    </FullWidthPageWrapper>
                  </LazyComponent>
                ),
              },
            ],
          },
          {
            path: 'fakturor',
            handle: { crumb: 'Fakturaöversikt' },
            element: (
              <LazyComponent>
                <FullWidthPageWrapper title="Fakturaöversikt">
                  <MyPagesAdminInvoiceOverviewPage />
                </FullWidthPageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'forsaljningsanalys',
            handle: { crumb: 'Försäljningsanalys' },
            element: (
              <LazyComponent>
                <FullWidthPageWrapper title="Försäljningsanalys">
                  <MyPagesSalesAnalysisPage />
                </FullWidthPageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'mrr-analys',
            handle: { crumb: 'MRR-analys' },
            element: (
              <LazyComponent>
                <FullWidthPageWrapper title="MRR-analys">
                  <MyPagesMrrAnalysisPage />
                </FullWidthPageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'leadsoversikt',
            handle: { crumb: 'Leadsöversikt' },
            element: (
              <LazyComponent>
                <FullWidthPageWrapper title="Leadsöversikt">
                  <LeadsContextDashboardProvider>
                    <MyPagesLeadsOverviewPage />
                  </LeadsContextDashboardProvider>
                </FullWidthPageWrapper>
              </LazyComponent>
            ),
          },
        ],
      },

      // Sprint 2 - Mina Sidor: Drift & administration
      {
        path: 'drift',
        handle: { crumb: 'Drift & admin' },
        children: [
          {
            path: 'anvandare',
            handle: { crumb: 'Användare' },
            children: [
              {
                index: true,
                element: (
                  <LazyComponent>
                    <FullWidthPageWrapper title="Användare och roller">
                      <MyPagesUsersListPage />
                    </FullWidthPageWrapper>
                  </LazyComponent>
                ),
              },
              {
                path: 'nytt',
                handle: { crumb: 'Ny användare' },
                element: (
                  <LazyComponent>
                    <FullWidthPageWrapper title="Ny användare">
                      <MyPagesUserFormPage mode="create" />
                    </FullWidthPageWrapper>
                  </LazyComponent>
                ),
              },
              {
                path: ':id',
                handle: { crumb: 'Användare' },
                element: (
                  <LazyComponent>
                    <FullWidthPageWrapper title="Användare">
                      <MyPagesUserFormPage mode="edit" />
                    </FullWidthPageWrapper>
                  </LazyComponent>
                ),
              },
            ],
          },
          {
            path: 'produkter',
            handle: { crumb: 'Produktregister' },
            children: [
              {
                index: true,
                element: (
                  <LazyComponent>
                    <FullWidthPageWrapper title="Produktregister">
                      <MyPagesProductsListPage />
                    </FullWidthPageWrapper>
                  </LazyComponent>
                ),
              },
              {
                path: 'nytt',
                handle: { crumb: 'Ny produkt' },
                element: (
                  <LazyComponent>
                    <FullWidthPageWrapper title="Ny produkt">
                      <MyPagesProductFormPage mode="create" />
                    </FullWidthPageWrapper>
                  </LazyComponent>
                ),
              },
              {
                path: ':id',
                handle: { crumb: 'Produkt' },
                element: (
                  <LazyComponent>
                    <FullWidthPageWrapper title="Produkt">
                      <MyPagesProductFormPage mode="edit" />
                    </FullWidthPageWrapper>
                  </LazyComponent>
                ),
              },
            ],
          },
          {
            path: 'utm',
            handle: { crumb: 'UTM-spårning' },
            element: (
              <LazyComponent>
                <FullWidthPageWrapper title="UTM Spårning">
                  <UtmOverviewPage />
                </FullWidthPageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'fortnox-status',
            handle: { crumb: 'Fortnox-status' },
            element: (
              <LazyComponent>
                <FullWidthPageWrapper title="Fortnox-status">
                  <MyPagesFortnoxStatusPage />
                </FullWidthPageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'svea-status',
            handle: { crumb: 'Svea-status' },
            element: (
              <LazyComponent>
                <FullWidthPageWrapper title="Svea-status">
                  <MyPagesSveaStatusPage />
                </FullWidthPageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'rebuild-status',
            handle: { crumb: 'Rebuild-all status' },
            element: (
              <LazyComponent>
                <FullWidthPageWrapper title="Rebuild-all status">
                  <MyPagesRebuildAllStatusPage />
                </FullWidthPageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'event-logs',
            handle: { crumb: 'Dashboard Event Logs' },
            element: (
              <LazyComponent>
                <FullWidthPageWrapper title="Dashboard Event Logs">
                  <MyPagesEventLogsPage />
                </FullWidthPageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'notifikationer',
            handle: { crumb: 'Notifikationsinställningar' },
            element: (
              <LazyComponent>
                <FullWidthPageWrapper title="Notifikationsinställningar">
                  <MyPagesNotificationSettingsPage />
                </FullWidthPageWrapper>
              </LazyComponent>
            ),
          },
        ],
      },
    ],
  },
  {
    path: 'partner',
    element: (
      <AuthGuard>
        <PartnerGuard>
          <DashboardLayout />
        </PartnerGuard>
      </AuthGuard>
    ),
    children: [
      { path: '/partner', element: <Navigate to="/partner/leads" /> },
      {
        path: 'sites',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Landningssidor">
              <SitesList />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'hemsidor',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Hemsidor">
              <HemsidorList />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'leads',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Kundförfrågningar">
              <LeadsList />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'leads/:id',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Lead Detaljer">
              <LeadDetailsPage />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      ...buildSettingsRoutes(),
    ],
  },
  {
    path: 'user',
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      { path: '/user', element: <Navigate to="/user/dashboard/default" /> },
      {
        path: 'dashboard',
        children: [
          {
            path: 'default',
            element: (
              <LazyComponent>
                <PageWrapper logo="true" title="Default">
                  <AdminDashboardPage />
                </PageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'ranking',
            element: (
              <LazyComponent>
                <PageWrapper logo="true" title="Ranking">
                  <RankingPage />
                </PageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'user-statistics',
            element: (
              <LazyComponent>
                <PageWrapper logo="true" title="User statistics">
                  <UserStatisticsPage />
                </PageWrapper>
              </LazyComponent>
            ),
          },
        ],
      },
      {
        path: 'statistik',
        children: [
          {
            path: 'landningsida',
            element: (
              <LazyComponent>
                <PageWrapper logo="true" title="Statistik - Landningssidor">
                  <AdminAnalyticsPage siteType="sites" />
                </PageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'hemsida',
            element: (
              <LazyComponent>
                <PageWrapper logo="true" title="Statistik - Hemsidor">
                  <AdminAnalyticsPage siteType="brandpages" />
                </PageWrapper>
              </LazyComponent>
            ),
          },
        ],
      },
      {
        path: 'sites',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Landningssidor">
              <SitesList />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'hemsidor',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Hemsidor">
              <HemsidorList />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'client',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Inlogg kunder">
              <AdminClientList />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'client/:id',
        element: (
          <LazyComponent>
            <PageWrapper logo="true" title="Inlogg kunder">
              <AssignSiteOwner />
            </PageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'kundföretag',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Kundföretag">
              <ClientsCompaniesList />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'leads',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Kundförfrågningar">
              <LeadsList />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'leads/:id',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Lead Detaljer">
              <LeadDetailsPage />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      ...buildSettingsRoutes(),
      {
        path: 'users',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Användare">
              <UsersList />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'users/:id',
        element: (
          <LazyComponent>
            <PageWrapper logo="true" title="Användare">
              <AssignSiteOwner />
            </PageWrapper>
          </LazyComponent>
        ),
      },
    ],
  },
  {
    path: '*',
    element: <AuthLayout />,
    children: [
      {
        path: '*',
        element: (
          <LazyComponent>
            <PageWrapper title="404">
              <Page404 />
            </PageWrapper>
          </LazyComponent>
        ),
      },
    ],
  },
];

export default routes;
